import { prenatalDefinition } from './definitions';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { PracticeConfiguration } from './rules';
import { Input } from '@/components/ui/input';
import { Field, FieldGroup, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { validatePayload } from './validate';
import type { EventType } from './catalog.generated';
import { activeEvents } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
export interface ProposedEntry {
  event_type: EventType | `CUSTOM.${string}`;
  schema_version?: number;
  subject_id: string;
  payload: Record<string, unknown>;
  source_ref?: { copied_from_event_id: string };
}
interface EntryField {
  key: string;
  label: string;
  numeric?: boolean;
  multiline?: boolean;
  boolean?: boolean;
  date?: boolean;
}
interface EntryGroup {
  type: EventType;
  label: string;
  fetus?: boolean;
  fields: EntryField[];
}
export const entryGroups: EntryGroup[] = [
  {
    type: 'PRENATAL.VISIT_DETAILS',
    label: 'Additional visit details',
    fields: [
      { key: 'labs_drawn', label: 'Labs drawn', boolean: true },
      { key: 'rhig_given', label: 'RhIG administration documented', boolean: true },
      { key: 'rhig_date', label: 'RhIG administration date', date: true },
      { key: 'internal_exam', label: 'Internal exam', multiline: true },
      { key: 'education', label: 'Education delivered', multiline: true },
      { key: 'orders', label: 'Orders documented', multiline: true },
      { key: 'urine_other', label: 'Other urine findings', multiline: true },
    ],
  },
  { type: 'VITALS.HEIGHT', label: 'Height', fields: [{ key: 'cm', label: 'Height (cm)', numeric: true }] },
  {
    type: 'VITALS.RR',
    label: 'Respiratory rate',
    fields: [
      { key: 'rate', label: 'Respirations (/min)', numeric: true },
      { key: 'quality', label: 'Respiratory quality' },
    ],
  },
  {
    type: 'VITALS.SPO2',
    label: 'Oxygen saturation',
    fields: [
      { key: 'value_pct', label: 'SpO₂ (%)', numeric: true },
      { key: 'site', label: 'SpO₂ measurement site' },
    ],
  },
  {
    type: 'NOTE.PROGRESS',
    label: 'Visit note',
    fields: [{ key: 'text', label: 'Narrative visit note', multiline: true }],
  },
  {
    type: 'EXAM.SECTION',
    label: 'Examination',
    fields: [
      { key: 'section', label: 'Exam section' },
      { key: 'findings', label: 'Exam findings (one finding per line)', multiline: true },
    ],
  },
  {
    type: 'VITALS.BP',
    label: 'Blood pressure (mmHg)',
    fields: [
      { key: 'systolic', label: 'Systolic', numeric: true },
      { key: 'diastolic', label: 'Diastolic', numeric: true },
    ],
  },
  { type: 'VITALS.WEIGHT', label: 'Weight', fields: [{ key: 'grams', label: 'Weight', numeric: true }] },
  { type: 'VITALS.PULSE', label: 'Maternal pulse', fields: [{ key: 'rate', label: 'Pulse (/min)', numeric: true }] },
  {
    type: 'VITALS.TEMP',
    label: 'Temperature',
    fields: [
      { key: 'value_c', label: 'Temperature (°C)', numeric: true },
      { key: 'method', label: 'Method' },
    ],
  },
  {
    type: 'PRENATAL.FUNDAL_HEIGHT',
    label: 'Fundal height',
    fields: [
      { key: 'cm', label: 'Fundal height (cm)', numeric: true },
      { key: 'landmark', label: 'Landmark' },
      { key: 'fingers', label: 'Fingers relative to landmark', numeric: true },
    ],
  },
  {
    type: 'PRENATAL.FHT',
    label: 'Fetal heart tones',
    fetus: true,
    fields: [
      { key: 'bpm', label: 'FHT (bpm)', numeric: true },
      { key: 'low_bpm', label: 'FHT range low (bpm)', numeric: true },
      { key: 'high_bpm', label: 'FHT range high (bpm)', numeric: true },
      { key: 'device', label: 'Listening device' },
    ],
  },
  {
    type: 'PRENATAL.FETAL_PRESENTATION',
    label: 'Presentation',
    fetus: true,
    fields: [
      { key: 'presentation', label: 'Presentation' },
      { key: 'position', label: 'Position' },
    ],
  },
  {
    type: 'PRENATAL.FETAL_MOVEMENT',
    label: 'Movement',
    fetus: true,
    fields: [{ key: 'value', label: 'Fetal movement' }],
  },
  {
    type: 'PRENATAL.EDEMA',
    label: 'Edema',
    fields: [
      { key: 'value', label: 'Edema assessment' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    type: 'LAB.URINE_DIP',
    label: 'Urine dip',
    fields: [
      { key: 'protein', label: 'Protein' },
      { key: 'glucose', label: 'Glucose' },
      { key: 'ketones', label: 'Ketones' },
      { key: 'nitrites', label: 'Nitrites' },
      { key: 'leukocytes', label: 'Leukocytes' },
      { key: 'blood', label: 'Blood' },
      { key: 'pH', label: 'pH' },
      { key: 'SG', label: 'Specific gravity' },
      { key: 'color', label: 'Color' },
      { key: 'clarity', label: 'Clarity' },
    ],
  },
];
export function parseEntries(values: Record<string, string>, subjects: ClinicalSubject[], fetusId: string) {
  const entries: ProposedEntry[] = [];
  const errors: string[] = [];
  for (const group of entryGroups) {
    const payload: Record<string, unknown> = {};
    for (const field of group.fields) {
      const value = values[`${group.type}.${field.key}`]?.trim();
      if (value)
        payload[field.key] =
          group.type === 'EXAM.SECTION' && field.key === 'findings'
            ? value
                .split('\n')
                .filter((v) => v.trim())
                .map((item) => ({ item: item.trim() }))
            : field.boolean
              ? value === 'true'
              : field.numeric
                ? Number(value)
                : value;
    }
    if (!Object.keys(payload).length) continue;
    if (group.type === 'VITALS.WEIGHT' && typeof payload.grams === 'number')
      payload.grams = Math.round(payload.grams * (values['weight-unit'] === 'kg' ? 1000 : 453.59237));
    const subject = subjects.find((s) =>
      group.fetus ? s.id === fetusId && s.subject_type === 'FETUS' : s.subject_type === 'MOTHER',
    );
    if (!subject) {
      errors.push(`Choose a subject for ${group.label}.`);
      continue;
    }
    const issues = validatePayload(group.type, payload);
    errors.push(...issues.map((issue) => `${group.label}: ${issue}`));
    if (!issues.length) entries.push({ event_type: group.type, subject_id: subject.id, payload });
  }
  return { entries, errors };
}
export function PrenatalEntry({
  subjects,
  onSave,
  disabled = false,
  events = [],
  configuration,
}: {
  subjects: ClinicalSubject[];
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  disabled?: boolean;
  events?: ClinicalEvent[];
  configuration?: PracticeConfiguration;
}) {
  const [reused, setReused] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const fetuses = subjects.filter((s) => s.subject_type === 'FETUS');
  const [fetusId, setFetusId] = useState(fetuses.length === 1 ? fetuses[0].id : '');
  const [time, setTime] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const reviewedLimits =
    configuration?.clinically_reviewed && configuration.clinical_references?.enabled
      ? configuration.clinical_references.bmi_limits
      : undefined;
  async function save() {
    const parsed = parseEntries(values, subjects, fetusId);
    const limits =
      configuration?.clinically_reviewed && configuration.clinical_references?.enabled
        ? configuration.clinical_references.bmi_limits
        : undefined;
    const weight = parsed.entries.find((e) => e.event_type === 'VITALS.WEIGHT')?.payload.grams;
    if (
      limits &&
      typeof weight === 'number' &&
      (weight / 1000 < limits.minWeightKg || weight / 1000 > limits.maxWeightKg)
    )
      parsed.errors.push(`Weight outside configured plausible range (${limits.minWeightKg}–${limits.maxWeightKg} kg)`);
    const height = parsed.entries.find((e) => e.event_type === 'VITALS.HEIGHT')?.payload.cm;
    if (typeof height === 'number' && !limits)
      parsed.errors.push(
        'Review and enable height plausibility limits in practice configuration before recording height.',
      );
    if (limits && typeof height === 'number' && (height < limits.minHeightCm || height > limits.maxHeightCm))
      parsed.errors.push(`Height outside plausible range (${limits.minHeightCm}–${limits.maxHeightCm} cm)`);
    if (!time || !Number.isFinite(new Date(time).getTime())) parsed.errors.push('Enter an observation date and time.');
    if (!parsed.entries.length && !parsed.errors.length) parsed.errors.push('Enter at least one observation.');
    setErrors(parsed.errors);
    if (parsed.errors.length) return;
    setSaving(true);
    try {
      await onSave(
        parsed.entries.map((e) => ({
          ...e,
          ...(reused[e.event_type] ? { source_ref: { copied_from_event_id: reused[e.event_type] } } : {}),
        })),
        new Date(time).toISOString(),
      );
      setValues({});
      setReused({});
    } catch {
      setErrors(['Save did not complete. Your entries are retained. Review the saved rows before retrying.']);
    } finally {
      setSaving(false);
    }
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      className="flex flex-col gap-6">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="observation-time">Observation time (this device’s local time)</FieldLabel>
          <Input
            className="min-h-11"
            id="observation-time"
            type="datetime-local"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            disabled={saving || disabled}
          />
        </Field>
        <Field>
          <FieldLabel>Fetus for fetal observations</FieldLabel>
          <Select
            value={fetusId}
            onValueChange={(id) => {
              setFetusId(id);
              setValues((v) =>
                Object.fromEntries(
                  Object.entries(v).filter(
                    ([key]) => !entryGroups.some((g) => g.fetus && key.startsWith(g.type + '.')),
                  ),
                ),
              );
              setReused((v) => Object.fromEntries(Object.entries(v).filter(([key]) => key === 'PRENATAL.EDEMA')));
            }}
            disabled={saving || disabled}>
            <SelectTrigger className="min-h-11" aria-label="Fetus for fetal observations">
              <SelectValue placeholder="Choose fetus" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {fetuses.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      </FieldGroup>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...entryGroups]
          .sort((a, b) => {
            const order = [
              'VITALS.BP',
              'VITALS.WEIGHT',
              'VITALS.PULSE',
              'VITALS.TEMP',
              'PRENATAL.FUNDAL_HEIGHT',
              'PRENATAL.FHT',
              'PRENATAL.FETAL_PRESENTATION',
              'PRENATAL.FETAL_MOVEMENT',
              'PRENATAL.EDEMA',
              'LAB.URINE_DIP',
              'PRENATAL.VISIT_DETAILS',
              'VITALS.HEIGHT',
              'VITALS.RR',
              'VITALS.SPO2',
              'EXAM.SECTION',
              'NOTE.PROGRESS',
            ];
            const configured = (configuration?.column_order ?? [])
              .map((key) => prenatalDefinition.columns.find((c) => c.key === key)?.eventType)
              .filter((value): value is NonNullable<typeof value> => !!value);
            const arranged = [...new Set([...configured, ...order])];
            return arranged.indexOf(a.type) - arranged.indexOf(b.type);
          })
          .map((group) => (
            <FieldSet
              key={group.type}
              disabled={saving || disabled || (group.type === 'VITALS.HEIGHT' && !reviewedLimits)}
              className={
                ['LAB.URINE_DIP', 'PRENATAL.VISIT_DETAILS'].includes(group.type) ? 'col-span-full' : undefined
              }>
              <FieldLegend>{group.label}</FieldLegend>
              {group.type === 'VITALS.HEIGHT' && !reviewedLimits && (
                <p className="text-sm text-muted-foreground">
                  Enable reviewed height plausibility limits in practice configuration to record height.
                </p>
              )}
              {['PRENATAL.FETAL_PRESENTATION', 'PRENATAL.FETAL_MOVEMENT', 'PRENATAL.EDEMA'].includes(group.type) &&
                (() => {
                  const subjectId = group.fetus ? fetusId : subjects.find((s) => s.subject_type === 'MOTHER')?.id;
                  const previous = activeEvents(events)
                    .filter(
                      (e) =>
                        e.event_type === group.type &&
                        e.subject_id === subjectId &&
                        Date.parse(e.occurred_at) <= Date.parse(time),
                    )
                    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0];
                  return previous ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setValues((v) => ({
                          ...v,
                          ...Object.fromEntries(
                            group.fields.map((f) => [
                              `${group.type}.${f.key}`,
                              previous.payload[f.key] === undefined ? '' : String(previous.payload[f.key]),
                            ]),
                          ),
                        }));
                        setReused((v) => ({ ...v, [group.type]: previous.id }));
                      }}>
                      Use last documented: {String(previous.payload[group.fields[0].key])} ·{' '}
                      {new Date(previous.occurred_at).toLocaleDateString()}
                    </Button>
                  ) : null;
                })()}
              {reused[group.type] && (
                <p className="text-sm text-muted-foreground">
                  Copied from a prior finding. Confirm it still applies before saving.
                </p>
              )}
              {group.type === 'VITALS.WEIGHT' && (
                <Field>
                  <FieldLabel>Weight unit</FieldLabel>
                  <Select
                    value={values['weight-unit'] ?? 'lb'}
                    onValueChange={(value) => setValues((v) => ({ ...v, 'weight-unit': value }))}>
                    <SelectTrigger className="min-h-11" aria-label="Weight unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="lb">lb</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <FieldGroup
                className={
                  ['LAB.URINE_DIP', 'PRENATAL.VISIT_DETAILS'].includes(group.type)
                    ? 'grid grid-cols-2 gap-4 md:grid-cols-4'
                    : undefined
                }>
                {group.fields.map((field) => {
                  const id = `${group.type}.${field.key}`;
                  return (
                    <Field key={id}>
                      <FieldLabel htmlFor={id}>{field.label}</FieldLabel>
                      {field.boolean ? (
                        <Select
                          value={values[id] || 'unset'}
                          onValueChange={(value) => setValues((v) => ({ ...v, [id]: value === 'unset' ? '' : value }))}>
                          <SelectTrigger aria-label={field.label} className="min-h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="unset">Not documented</SelectItem>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      ) : field.multiline ? (
                        <Textarea
                          id={id}
                          value={values[id] ?? ''}
                          onChange={(e) => setValues((v) => ({ ...v, [id]: e.target.value }))}
                          rows={3}
                        />
                      ) : (
                        <Input
                          className="min-h-11"
                          id={id}
                          type={field.date ? 'date' : field.numeric ? 'number' : 'text'}
                          step={field.numeric ? 'any' : undefined}
                          value={values[id] ?? ''}
                          onChange={(e) => {
                            setValues((v) => ({ ...v, [id]: e.target.value }));
                            setReused((v) => {
                              const next = { ...v };
                              delete next[group.type];
                              return next;
                            });
                          }}
                          inputMode={field.numeric ? 'decimal' : undefined}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const form = e.currentTarget.form;
                              const inputs = Array.from(
                                form?.querySelectorAll<HTMLInputElement>(
                                  'input:not([disabled]),textarea:not([disabled])',
                                ) ?? [],
                              );
                              inputs[inputs.indexOf(e.currentTarget) + 1]?.focus();
                            }
                          }}
                        />
                      )}
                      {group.type === 'LAB.URINE_DIP' &&
                        ['protein', 'glucose', 'ketones', 'blood', 'leukocytes', 'nitrites'].includes(field.key) && (
                          <div className="flex flex-wrap gap-1" role="group" aria-label={`${field.label} choices`}>
                            {(field.key === 'nitrites'
                              ? ['Negative', 'Positive']
                              : ['Negative', 'Trace', '1+', '2+', '3+', '4+']
                            ).map((choice) => (
                              <Button
                                key={choice}
                                type="button"
                                variant={values[id] === choice ? 'default' : 'outline'}
                                className="min-h-11"
                                aria-pressed={values[id] === choice}
                                onClick={() => setValues((v) => ({ ...v, [id]: v[id] === choice ? '' : choice }))}>
                                {choice}
                              </Button>
                            ))}
                          </div>
                        )}
                    </Field>
                  );
                })}
              </FieldGroup>
            </FieldSet>
          ))}
      </div>
      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            <ul>
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      <Button type="submit" disabled={saving || disabled} className="min-h-11">
        {saving ? 'Saving observations…' : 'Save observations'}
      </Button>
    </form>
  );
}
